import axios from 'axios'
import { useRef, useState } from 'react'
import { config } from '../config'
import { IGenericObject } from '@types'

export interface IRestMethods<K = IGenericObject> {
  get: (
    params: IGenericObject,
    id?: string | number,
    postUrl?: string
  ) => Promise<K>
  post: (
    body: IGenericObject | FormData | string,
    postUrl?: string
  ) => Promise<K>
  put: (
    body: IGenericObject | FormData | string,
    id?: string | number,
    postUrl?: string
  ) => Promise<K>
  patch: (
    body: IGenericObject | FormData | string,
    id?: string | number,
    postUrl?: string
  ) => Promise<K>
  remove: (id: string | number, postUrl?: string) => Promise<K>
  abortRequest: (method: 'POST', completeUrl: string) => void
  loading: boolean
}

/**
 * Hook per la gestione delle richieste rest
 * @param url {string=} url del'undpoint da chiamare
 * @param option {{ controlled: boolean } | undefined} parametro contenente le opzioni per le request
 * @return {IRestMethods}
 */

const useRest = <K = IGenericObject>(
  url?: string,
  option?: { controlled: boolean }
): IRestMethods<K> => {
  const baseUrl = config.endpoint
  const fullUrl = baseUrl + url || ''

  const headers = {
    'Content-Type': 'application/json',
  }

  const abortMapRef = useRef<IGenericObject<AbortController | undefined>>({})

  const [loading, setLoading] = useState(false)

  /**
   * Funzione per una richiesta GET al endpoint fornito
   * @param params {IGenericObject} parametri di ricerca o di aggregazione
   * @param id {string | number=} parametro per una richiesta in GET per un singolo elemento
   * @param postUrl {string} parametro per aggiungere al url ulteriori percorsi
   * @return {Promise<any>} dati ricevuti dal backend
   */
  const get = (
    params: IGenericObject = {},
    id?: string | number,
    postUrl?: string
  ): Promise<K> => {
    setLoading(true)
    return axios
      .get(`${fullUrl}${id ? `/${id}` : ''}${postUrl ? `${postUrl}` : ''}`, {
        params: params,
        headers: { ...headers },
      })
      .then(({ data }) => {
        setLoading(false)
        return data as K
      })
      .catch((err: any) => {
        setLoading(false)
        console.error(err)
        throw new Error(err.message)
      })
  }

  /**
   * Funzione per le richieste con metodo POST
   * @param body {IGenericObject} parametri di ricerca
   * @param postUrl {string} url ulteriore da aggiungere alla richiesta
   * @return {Promise<any>} dati ricevuti dal backend
   */
  const post = (
    body: IGenericObject | FormData | string = {},
    postUrl?: string
  ): Promise<K> => {
    setLoading(true)
    let signal: undefined | AbortSignal
    if (option && option.controlled) {
      const controller = new AbortController()
      abortMapRef.current = {
        ...abortMapRef.current,
        [`method:POST,url:${fullUrl}${postUrl || ''}`]: controller,
      }
      signal = controller.signal
    }

    return axios
      .post(`${fullUrl}${postUrl || ''}`, body, {
        headers: { ...headers },
        signal,
      })
      .then(({ data }) => {
        setLoading(false)
        if (option && option.controlled)
          abortMapRef.current = {
            ...abortMapRef.current,
            [`method:POST,url:${fullUrl}${postUrl || ''}`]: undefined,
          }
        return data as K
      })
      .catch((err: any) => {
        setLoading(false)
        if (option && option.controlled)
          abortMapRef.current = {
            ...abortMapRef.current,
            [`method:POST,url:${fullUrl}${postUrl || ''}`]: undefined,
          }
        console.error(err)
        throw new Error(err.message)
      })
  }

  /**
   * Funzione per le richieste con metodo PUT
   * @param id {string | number=} parametro per una richiesta in PUT per un singolo elemento
   * @param body {IGenericObject} parametri di ricerca
   * @param postUrl {string} url ulteriore da aggiungere alla richiesta
   * @return {Promise<any>} dati ricevuti dal backend
   */
  const put = (
    body: IGenericObject | FormData | string = {},
    id?: string | number,
    postUrl?: string
  ): Promise<K> => {
    setLoading(true)
    return axios
      .put(`${fullUrl}${postUrl || ''}/${id || ''}`, body, {
        headers: { ...headers },
      })
      .then(({ data }) => {
        setLoading(false)
        return data as K
      })
      .catch((err: any) => {
        setLoading(false)
        console.error(err)
        throw new Error(err.message)
      })
  }

  /**
   * Funzione per le richieste con metodo PATCH
   * @param id {string | number=} parametro per una richiesta in PATCH per un singolo elemento
   * @param body {IGenericObject} parametri di ricerca
   * @param postUrl {string} url ulteriore da aggiungere alla richiesta
   * @return {Promise<any>} dati ricevuti dal backend
   */
  const patch = (
    body: IGenericObject | FormData | string = {},
    id?: string | number,
    postUrl?: string
  ): Promise<K> => {
    setLoading(true)
    return axios
      .patch(`${fullUrl}${postUrl || ''}/${id || ''}`, body, {
        headers: { ...headers },
      })
      .then(({ data }) => {
        setLoading(false)
        return data as K
      })
      .catch((err: any) => {
        setLoading(false)
        console.error(err)
        throw new Error(err.message)
      })
  }

  /**
   * Funzione per le richieste con metodo DELETE
   * @param id {string | number} parametro per una richiesta in PATCH per un singolo elemento
   * @param postUrl {string} url ulteriore da aggiungere alla richiesta
   * @return {Promise<any>} dati ricevuti dal backend
   */
  const remove = (id: string | number, postUrl?: string): Promise<K> => {
    setLoading(true)
    return axios
      .delete(`${fullUrl}${postUrl || ''}/${id}`, {
        headers: { ...headers },
      })
      .then(({ data }) => {
        setLoading(false)
        return data as K
      })
      .catch((err: any) => {
        setLoading(false)
        console.error(err)
        throw new Error(err.message)
      })
  }
  /**
   * Funzione per interrompere una richiesta Rest
   * @param method {'POST'} metodo della richiesta
   * @param url {string} url completo per l'identificazione della richiesta da cancellare
   */
  const abortRequest = (method: 'POST', url: string) => {
    if (option && option.controlled) {
      const abortController = abortMapRef.current[`method:${method},url:${url}`]
      if (abortController) {
        abortController.abort()
        abortMapRef.current = {
          ...abortMapRef.current,
          [`method:${method},url:${url}`]: undefined,
        }
      }
    }
  }

  return {
    get,
    post,
    put,
    patch,
    remove,
    abortRequest,
    loading,
  }
}

export default useRest
